import { toNumber, toString, uniqBy } from 'lodash';
import { Allocation } from '../domain/PurchaseOrder/Allocation';
import { Pricing } from '../domain/PurchaseOrder/Pricing';
import { PurchaseOrder } from '../domain/PurchaseOrder/PurchaseOrder';
import {
  FormAllocation,
  FormPricing,
  LegalEngineRequest,
  PurchaseOrderFormValues,
} from '../types/types';

const toNumberOrUndefined = (maybeNumber?: any) => {
  const num = toNumber(maybeNumber);

  return num > 0 ? num : undefined;
};

const firstDefinedValue = (...values: any[]) =>
  values.find((value) => value !== undefined && value !== null && value !== '');

const toOptionalString = (value: any): string | undefined =>
  value ? String(value) : undefined;

const toAllocations = (
  formAllocations: FormAllocation[],
  formPricing: FormPricing[],
  pricingFields: Array<keyof FormPricing>,
  STORE_NUMBER_KEY?: string
): Allocation[] => {
  return formAllocations?.reduce(
    (allocations: Allocation[], formAllocation: FormAllocation) => {
      const foundFormPricing: FormPricing | undefined =
        formPricing &&
        formPricing.find((p: FormPricing) => p.state === formAllocation.state);

      let pricing: Pricing = {};

      if (foundFormPricing) {
        pricingFields.forEach((field: keyof FormPricing) => {
          if (foundFormPricing[field]) {
            pricing = {
              ...pricing,
              [field]: toNumberOrUndefined(foundFormPricing[field]),
            };
          }
        });
      }

      const allocation: Allocation = {
        draftPoDetailsId: formAllocation.draftPoDetailsId || undefined,
        // will change this
        [STORE_NUMBER_KEY || 'storeNumber']:
          formAllocation.storeNum || undefined,
        quantity: toNumberOrUndefined(formAllocation.quantity),
        state: formAllocation.state || undefined,
        pricing,
      };

      return [...allocations, allocation];
    },
    []
  );
};

export const toPurchaseOrder = (
  formValues: PurchaseOrderFormValues
  // eslint-disable-next-line
): PurchaseOrder => {
  const pricingFields: Array<keyof FormPricing> = [
    'totalRtoPrice',
    'cashPrice',
    'weeklyRent',
    'weeklyTerm',
    'biWeeklyRent',
    'biWeeklyTerm',
    'semiMonthlyRent',
    'semiMonthlyTerm',
    'monthlyRent',
    'monthlyTerm',
  ];

  return {
    id: formValues.id || undefined,
    purchaseOrderSubType: formValues.purchaseOrderSubType || undefined,
    referenceNumber: formValues.referenceNumber || undefined,
    startClassificationsSelect:
      formValues.startClassificationsSelect || undefined,
    supplier: formValues.supplier || undefined,
    brand: formValues.brand || undefined,
    model: formValues.model || undefined,
    itemCondition: formValues.itemCondition || undefined,
    department: formValues.department
      ? String(formValues.department)
      : undefined,
    subDepartment: formValues.subDepartment
      ? String(formValues.subDepartment)
      : undefined,
    bracket: formValues.bracket ? String(formValues.bracket) : undefined,
    itemType: formValues.itemType || undefined,
    itemSubType: formValues.itemSubType || undefined,
    description: formValues.description || undefined,
    supplierOrderNo: formValues.supplierOrderNo || undefined,
    allocations: toAllocations(
      formValues.allocations,
      formValues.pricing,
      pricingFields
    ),
    totalQuantity: toNumberOrUndefined(formValues.totalQuantity),
    costPerItem: toNumberOrUndefined(formValues.costPerItem),
    totalCost: toNumberOrUndefined(formValues.totalCost),
    costOfAlignment: formValues.costOfAlignment
      ? Number(formValues.costOfAlignment)
      : undefined,
    ...(typeof formValues.rmsItemNumber === 'string' &&
      formValues.rmsItemNumber !== null &&
      formValues.rmsItemNumber !== undefined && {
        rmsItemNumber: formValues.rmsItemNumber,
      }),
    ...(formValues.scanMatchIdentifier && {
      scanMatchIdentifier: formValues.scanMatchIdentifier,
    }),
  };
};

const LOCAL_SOURCE_MAX_PRODUCTS = 4;

const hasDefinedValue = (value: any): boolean =>
  value !== undefined && value !== null && value !== '';

const getLocalSourceProductIndexes = (
  formValues: PurchaseOrderFormValues
): number[] => {
  if (Array.isArray(formValues.localSourceProductIndexes)) {
    return formValues.localSourceProductIndexes
      .map((index: any) => Number(index))
      .filter(
        (index: number) => index >= 0 && index < LOCAL_SOURCE_MAX_PRODUCTS
      );
  }

  return [0, 1, 2, 3].filter((index) => {
    const product = index === 0 ? formValues : formValues.products?.[index];
    const allocation = formValues.allocations?.[index];

    return (
      hasDefinedValue(product?.supplier) ||
      hasDefinedValue(product?.brand) ||
      hasDefinedValue(product?.model) ||
      hasDefinedValue(product?.department) ||
      hasDefinedValue(product?.costPerItem) ||
      hasDefinedValue(allocation?.quantity)
    );
  });
};

const getLocalSourceProductValue = (
  formValues: PurchaseOrderFormValues,
  index: number,
  fieldName: string
) =>
  index === 0
    ? formValues[fieldName]
    : formValues.products?.[index]?.[fieldName];

const toLocalSourcePricing = (
  pricingSource: any,
  formPricing?: FormPricing
): Pricing => {
  const rateTerm = pricingSource?.rateTerm || {};
  const pricing = pricingSource?.pricing || formPricing || {};

  return {
    totalRtoPrice: toNumberOrUndefined(
      firstDefinedValue(rateTerm.totalCost, pricing.totalRtoPrice)
    ),
    cashPrice: toNumberOrUndefined(
      firstDefinedValue(rateTerm.cashPrice, pricing.cashPrice)
    ),
    weeklyRent: toNumberOrUndefined(
      firstDefinedValue(rateTerm.weeklyRate, pricing.weeklyRent)
    ),
    weeklyTerm: toNumberOrUndefined(
      firstDefinedValue(rateTerm.weeklyTerm, pricing.weeklyTerm)
    ),
    biWeeklyRent: toNumberOrUndefined(
      firstDefinedValue(rateTerm.biWeeklyRate, pricing.biWeeklyRent)
    ),
    biWeeklyTerm: toNumberOrUndefined(
      firstDefinedValue(rateTerm.biWeeklyTerm, pricing.biWeeklyTerm)
    ),
    semiMonthlyRent: toNumberOrUndefined(
      firstDefinedValue(rateTerm.semiMonthlyRate, pricing.semiMonthlyRent)
    ),
    semiMonthlyTerm: toNumberOrUndefined(
      firstDefinedValue(rateTerm.semiMonthlyTerm, pricing.semiMonthlyTerm)
    ),
    monthlyRent: toNumberOrUndefined(
      firstDefinedValue(rateTerm.monthlyRate, pricing.monthlyRent)
    ),
    monthlyTerm: toNumberOrUndefined(
      firstDefinedValue(rateTerm.monthlyTerm, pricing.monthlyTerm)
    ),
  };
};

const getPricingForState = (
  formValues: PurchaseOrderFormValues,
  state?: string
): FormPricing | undefined =>
  formValues.pricing?.find((pricing: FormPricing) => pricing.state === state);

const toLocalSourcePurchaseOrderDetails = (
  formValues: PurchaseOrderFormValues
) => {
  const productIndexes = getLocalSourceProductIndexes(formValues);
  const pricingItems = Array.isArray(formValues.items) ? formValues.items : [];

  return productIndexes.map((productIndex: number, position: number) => {
    const pricingItem = pricingItems[position] || {};
    const allocation = formValues.allocations?.[productIndex] || {};
    const firstStoreAllocation =
      formValues.allocations?.find((item) => hasDefinedValue(item.storeNum)) ||
      {};
    const state = firstDefinedValue(
      pricingItem.state,
      firstStoreAllocation.state,
      allocation.state
    );
    const quantity = toNumberOrUndefined(allocation.quantity);
    const costPerItem = toNumberOrUndefined(
      firstDefinedValue(
        pricingItem.purchaseCost,
        pricingItem.costPerItem,
        getLocalSourceProductValue(formValues, productIndex, 'costPerItem')
      )
    );

    return {
      supplier: firstDefinedValue(
        pricingItem.supplier,
        getLocalSourceProductValue(formValues, productIndex, 'supplier')
      ),
      brand: firstDefinedValue(
        pricingItem.brand,
        getLocalSourceProductValue(formValues, productIndex, 'brand')
      ),
      model: firstDefinedValue(
        pricingItem.modelNumber,
        getLocalSourceProductValue(formValues, productIndex, 'model')
      ),
      itemCondition: firstDefinedValue(
        pricingItem.itemCondition,
        getLocalSourceProductValue(formValues, productIndex, 'itemCondition'),
        formValues.itemCondition
      ),
      supplierOrderNo: getLocalSourceProductValue(
        formValues,
        productIndex,
        'supplierOrderNo'
      ),
      description: firstDefinedValue(
        pricingItem.productDescription,
        getLocalSourceProductValue(formValues, productIndex, 'description')
      ),
      department: String(
        firstDefinedValue(
          pricingItem.department,
          getLocalSourceProductValue(formValues, productIndex, 'department')
        )
      ),
      subDepartment: String(
        firstDefinedValue(
          pricingItem.subDepartment,
          getLocalSourceProductValue(formValues, productIndex, 'subDepartment')
        )
      ),
      bracket: String(
        firstDefinedValue(
          pricingItem.bracket,
          getLocalSourceProductValue(formValues, productIndex, 'bracket')
        )
      ),
      itemType: firstDefinedValue(
        pricingItem.itemTypeCode,
        getLocalSourceProductValue(formValues, productIndex, 'itemType')
      ),
      itemSubType: firstDefinedValue(
        pricingItem.itemSubTypeCode,
        getLocalSourceProductValue(formValues, productIndex, 'itemSubType')
      ),
      quantity,
      costPerItem,
      totalCost:
        quantity && costPerItem
          ? Number((quantity * costPerItem).toFixed(2))
          : undefined,
      rmsItemNumber: toOptionalString(pricingItem.rmsItemNumber),
      scanMatchIdentifier:
        (formValues as any).scanMatchIdentifierPerItem?.[productIndex] ||
        formValues.scanMatchIdentifier,
      storeNumber: firstDefinedValue(
        pricingItem.storeNumber,
        firstStoreAllocation.storeNum,
        allocation.storeNum
      ),
      state,
      pricing: toLocalSourcePricing(
        pricingItem,
        getPricingForState(formValues, state)
      ),
    };
  });
};

const sumLocalSourceField = (
  purchaseOrderDetails: any[],
  fieldName: string
): number =>
  purchaseOrderDetails.reduce(
    (total: number, purchaseOrderDetail: any) =>
      total + (purchaseOrderDetail[fieldName] || 0),
    0
  );

const toLocalSourceAllocations = (purchaseOrderDetails: any[]) =>
  purchaseOrderDetails.map((purchaseOrderDetail: any) => ({
    storeNumber: purchaseOrderDetail.storeNumber,
    quantity: purchaseOrderDetail.quantity,
    state: purchaseOrderDetail.state,
    pricing: purchaseOrderDetail.pricing,
  }));

const toLocalSourceFirstProductFields = (purchaseOrderDetail: any) => ({
  supplier: purchaseOrderDetail.supplier || undefined,
  brand: purchaseOrderDetail.brand || undefined,
  model: purchaseOrderDetail.model || undefined,
  itemCondition: purchaseOrderDetail.itemCondition || undefined,
  department: purchaseOrderDetail.department || undefined,
  subDepartment: purchaseOrderDetail.subDepartment || undefined,
  bracket: purchaseOrderDetail.bracket || undefined,
  itemType: purchaseOrderDetail.itemType || undefined,
  itemSubType: purchaseOrderDetail.itemSubType || undefined,
  description: purchaseOrderDetail.description || undefined,
  supplierOrderNo: purchaseOrderDetail.supplierOrderNo || undefined,
  costPerItem: purchaseOrderDetail.costPerItem || undefined,
  ...(purchaseOrderDetail.rmsItemNumber && {
    rmsItemNumber: purchaseOrderDetail.rmsItemNumber,
  }),
});

export const toLocalSourcePurchaseOrder = (
  formValues: PurchaseOrderFormValues
): PurchaseOrder => {
  const purchaseOrderDetails = toLocalSourcePurchaseOrderDetails(formValues);
  const firstPurchaseOrderDetail = purchaseOrderDetails[0] || {};
  const totalQuantity = sumLocalSourceField(purchaseOrderDetails, 'quantity');
  const totalCost = sumLocalSourceField(purchaseOrderDetails, 'totalCost');

  return {
    id: formValues.id || undefined,
    draftId: formValues.id || formValues.draftId || undefined,
    purchaseOrderSubType: formValues.purchaseOrderSubType || undefined,
    referenceNumber: formValues.referenceNumber || undefined,
    startClassificationsSelect:
      formValues.startClassificationsSelect || undefined,
    ...toLocalSourceFirstProductFields(firstPurchaseOrderDetail),
    allocations: toLocalSourceAllocations(purchaseOrderDetails),
    totalQuantity: totalQuantity || undefined,
    totalCost: totalCost || undefined,
    ...(formValues.spendLimitId && {
      spendLimitId: formValues.spendLimitId,
    }),
    ...(formValues.scanMatchIdentifier && {
      scanMatchIdentifier: formValues.scanMatchIdentifier,
    }),
    purchaseOrderDetails,
  } as any;
};

export const toPurchaseOrderFormValues = (
  purchaseOrder: PurchaseOrder
  // eslint-disable-next-line
): PurchaseOrderFormValues => {
  const rawAllocations: FormAllocation[] = purchaseOrder?.allocations?.map(
    (a: Allocation): FormAllocation => ({
      draftPoDetailsId: toString(a?.draftPoDetailsId) || '',
      quantity: toString(a?.quantity),
      storeNum: a?.storeNum || '',
      state: a?.state || '',
    })
  );

  // Consolidate same-store allocations only for draft POs.
  // Submitted multi-item POs need per-product allocations preserved.
  const isDraft =
    !purchaseOrder?.poStatus || purchaseOrder?.poStatus === 'draft';
  const allocations: FormAllocation[] = rawAllocations
    ? isDraft
      ? Object.values(
          rawAllocations.reduce(
            (acc: Record<string, FormAllocation>, alloc: FormAllocation) => {
              const key =
                alloc.storeNum ||
                alloc.draftPoDetailsId ||
                Math.random().toString();
              if (acc[key] && alloc.storeNum) {
                acc[key] = {
                  ...acc[key],
                  quantity: toString(
                    Number(acc[key].quantity || 0) + Number(alloc.quantity || 0)
                  ),
                };
              } else {
                acc[key] = alloc;
              }
              return acc;
            },
            {}
          )
        )
      : rawAllocations
    : [];

  const pricing: FormPricing[] = purchaseOrder?.allocations
    ?.filter((a: Allocation): boolean => !!a?.state)
    .map(
      (a: Allocation): FormPricing => ({
        state: a.state || '',
        weeklyRent: a.pricing ? toString(a.pricing.weeklyRent) : '',
        weeklyTerm: a.pricing ? toString(a.pricing.weeklyTerm) : '',
        biWeeklyRent: a.pricing ? toString(a.pricing.biWeeklyRent) : '',
        biWeeklyTerm: a.pricing ? toString(a.pricing.biWeeklyTerm) : '',
        semiMonthlyRent: a.pricing ? toString(a.pricing.semiMonthlyRent) : '',
        semiMonthlyTerm: a.pricing ? toString(a.pricing.semiMonthlyTerm) : '',
        monthlyRent: a.pricing ? toString(a.pricing.monthlyRent) : '',
        monthlyTerm: a.pricing ? toString(a.pricing.monthlyTerm) : '',
        cashPrice: a.pricing ? toString(a.pricing.cashPrice) : '',
        totalRtoPrice: a.pricing ? toString(a.pricing.totalRtoPrice) : '',
      })
    );

  const uniquePricing = uniqBy(pricing, (p: FormPricing) => {
    return p.state;
  });

  return {
    id: purchaseOrder.id || '',
    purchaseOrderSubType: purchaseOrder.purchaseOrderSubType || '',
    referenceNumber: purchaseOrder.referenceNumber || '',
    startClassificationsSelect: purchaseOrder.startClassificationsSelect || '',
    supplier: purchaseOrder.supplier || '',
    brand: purchaseOrder.brand || '',
    model: purchaseOrder.model || '',
    itemCondition: purchaseOrder.itemCondition || '',
    department: purchaseOrder.department || '',
    subDepartment: purchaseOrder.subDepartment || '',
    bracket: purchaseOrder.bracket || '',
    itemType: purchaseOrder.itemType || '',
    itemSubType: purchaseOrder.itemSubType || '',
    description: purchaseOrder.description || '',
    supplierOrderNo: purchaseOrder.supplierOrderNo || '',
    allocations: allocations,
    pricing: uniquePricing,
    totalQuantity: toString(purchaseOrder.totalQuantity),
    costPerItem: toString(purchaseOrder.costPerItem),
    totalCost: toString(purchaseOrder.totalCost),
    poStatus: purchaseOrder.poStatus || '',
    isCanceled: purchaseOrder.isCanceled || false,
    costOfAlignment: purchaseOrder.costOfAlignment || 0,
    createdBy: purchaseOrder.createdBy || '',
    rmsItemNumber: purchaseOrder.rmsItemNumber || '',
    isRcptAvailable: purchaseOrder.isRcptAvailable || false,
    purchaseOrderId: purchaseOrder.purchaseOrderId || '',
    spendLimitId: (purchaseOrder as any).spendLimitId || '',
    purchaseOrderDetails: purchaseOrder.purchaseOrderDetails || [],
  };
};

export const toLegalEngineRequest = (
  formValues: PurchaseOrderFormValues
): LegalEngineRequest => {
  const pricingFields: Array<keyof FormPricing> = [
    'weeklyRent',
    'weeklyTerm',
    'totalRtoPrice',
  ];

  return {
    itemCondition: formValues.itemCondition,
    department: formValues.department
      ? String(formValues.department)
      : formValues.department,
    subDepartment: formValues.subDepartment
      ? String(formValues.subDepartment)
      : formValues.subDepartment,
    allocations: toAllocations(
      formValues.allocations,
      formValues.pricing,
      pricingFields,
      'storeNum' // will change it
    ),
    costPerItem: toNumber(formValues.costPerItem),
    itemType: formValues.itemType,
    itemSubType: formValues.itemSubType,
    bracket: formValues.bracket
      ? String(formValues.bracket)
      : formValues.bracket,
    rmsItemNumber: ['0', '', null, undefined].includes(
      formValues.rmsItemNumber as any
    )
      ? ''
      : formValues.rmsItemNumber,
  };
};

const toItemLegalEngineRequest = (
  formValues: PurchaseOrderFormValues,
  item: any,
  index: number
): LegalEngineRequest => {
  const firstAllocation = formValues.allocations?.[0] || {};
  const allocationForItem = formValues.allocations?.[index] || firstAllocation;
  const rateTerm = item?.rateTerm || {};
  const state = firstDefinedValue(
    item?.state,
    allocationForItem?.state,
    firstAllocation?.state
  );
  const itemPricing = Array.isArray(item?.pricing) ? undefined : item?.pricing;
  const pricing = itemPricing || getPricingForState(formValues, state) || {};
  const allocationQuantity = firstDefinedValue(
    item?.quantity,
    allocationForItem?.quantity
  );

  // Use form values for classifications/costPerItem (IDs like "34", "340")
  // NOT product-catalog names (like "MOBILE", "CELLPHONES")
  const formDepartment = getLocalSourceProductValue(
    formValues,
    index,
    'department'
  );
  const formSubDepartment = getLocalSourceProductValue(
    formValues,
    index,
    'subDepartment'
  );
  const formBracket = getLocalSourceProductValue(formValues, index, 'bracket');
  const formItemType = getLocalSourceProductValue(
    formValues,
    index,
    'itemType'
  );
  const formItemSubType = getLocalSourceProductValue(
    formValues,
    index,
    'itemSubType'
  );
  const formCostPerItem = getLocalSourceProductValue(
    formValues,
    index,
    'costPerItem'
  );

  return {
    itemCondition:
      getLocalSourceProductValue(formValues, index, 'itemCondition') ||
      formValues.itemCondition,
    department: String(formDepartment || formValues.department),
    subDepartment: String(formSubDepartment || formValues.subDepartment),
    allocations: [
      {
        storeNum: firstDefinedValue(
          item?.storeNumber,
          allocationForItem?.storeNum,
          firstAllocation?.storeNum
        ),
        quantity: toNumberOrUndefined(allocationQuantity),
        state,
        pricing: {
          weeklyRent: toNumberOrUndefined(
            firstDefinedValue(rateTerm.weeklyRate, pricing.weeklyRent)
          ),
          weeklyTerm: toNumberOrUndefined(
            firstDefinedValue(rateTerm.weeklyTerm, pricing.weeklyTerm)
          ),
          totalRtoPrice: toNumberOrUndefined(
            firstDefinedValue(rateTerm.totalCost, pricing.totalRtoPrice)
          ),
        },
      },
    ],
    costPerItem: toNumber(formCostPerItem || formValues.costPerItem),
    itemType: formItemType || formValues.itemType,
    itemSubType: formItemSubType || formValues.itemSubType,
    bracket: String(formBracket || formValues.bracket),
    rmsItemNumber:
      toOptionalString(item?.rmsItemNumber) ||
      toOptionalString(formValues.rmsItemNumber) ||
      '',
  };
};

export const toLocalSourceLegalEngineRequests = (
  formValues: PurchaseOrderFormValues
): LegalEngineRequest[] => {
  const productIndexes = getLocalSourceProductIndexes(formValues);
  const pricingItems = Array.isArray(formValues.items) ? formValues.items : [];

  return productIndexes.length
    ? productIndexes.map((productIndex: number, position: number) =>
        toItemLegalEngineRequest(
          formValues,
          pricingItems[position] ||
            (productIndex === 0
              ? formValues
              : formValues.products?.[productIndex]),
          productIndex
        )
      )
    : [toLegalEngineRequest(formValues)];
};
